import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import axios from "axios";
import { useTranslation } from "react-i18next";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const PER_PAGE = 10;


/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {

    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
        return "0";
    }

    return new Intl.NumberFormat("en-BD", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(number);
};


const money = (value, symbol = "৳") => {

    return `${symbol}${numberFormat(value)}`;
};


const safeText = (
    value,
    fallback = "—"
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    return value;
};


const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const date = new Date(
            String(value).replace(
                " ",
                "T"
            )
        );

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleString(
            "en-BD",
            {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }
        );

    } catch (error) {

        return value;
    }
};


/* =========================================================
   STATUS
========================================================= */

const getStatus = (status) => {

    const value =
        String(
            status ?? ""
        )
            .trim()
            .toLowerCase();


    if (
        value === "1" ||
        value === "success" ||
        value === "successful" ||
        value === "completed"
    ) {

        return {
            text: "Success",
            className: "success"
        };
    }


    if (
        value === "0" ||
        value === "failed" ||
        value === "fail"
    ) {

        return {
            text: "Failed",
            className: "failed"
        };
    }


    if (
        value === "pending"
    ) {

        return {
            text: "Pending",
            className: "pending"
        };
    }


    if (
        value === "2" ||
        value === "cancel" ||
        value === "cancelled" ||
        value === "canceled"
    ) {

        return {
            text: "Cancelled",
            className: "cancelled"
        };
    }


    return {
        text: status || "Unknown",
        className: "default"
    };
};


/* =========================================================
   COMPONENT
========================================================= */

const BasicDatatable = () => {

    const { t } = useTranslation();

    const { CurrencySymbol } =
        "৳";


    /* =====================================================
       STATE
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [pageError, setPageError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);


    /* =====================================================
       LOCAL STORAGE
    ===================================================== */

    const authToken =
        localStorage.getItem(
            "auth_token"
        );


    const selectedCurrency =
        localStorage.getItem(
            "selectedCurrency"
        );


    const currency =
        selectedCurrency
            ? selectedCurrency.replace(
                /"/g,
                ""
            )
            : "BDT";


    /*
     * Global CurrencySymbol থাকলে সেটা ব্যবহার হবে।
     * না থাকলে ৳ দেখাবে।
     */

    const currencySymbol =
        CurrencySymbol || "৳";


    /* =====================================================
       FETCH TRANSACTION HISTORY
    ===================================================== */

    const fetchTransactions =
        async () => {

            if (!authToken) {

                setLoading(false);

                setPageError(
                    "Your login session was not found. Please login again."
                );

                return;
            }


            try {

                setLoading(true);

                setPageError("");


                const response =
                    await axios.post(
                        "/apps-transaction-history",
                        {
                            key_s:
                                API_KEY,

                            currency:
                                currency,
                        }
                    );


                const result =
                    response?.data || {};


                if (
                    result.status ===
                        "success" ||
                    Array.isArray(
                        result.data
                    )
                ) {

                    const rows =
                        Array.isArray(
                            result.data
                        )
                            ? result.data
                            : [];


                    setIncome(
                        rows
                    );

                } else {

                    setIncome([]);

                    setPageError(
                        result.message ||
                        "Unable to load transaction history."
                    );
                }


            } catch (error) {

                console.error(
                    "Transaction History Error:",
                    error
                );


                setIncome([]);

                setPageError(
                    error?.response?.data
                        ?.message ||
                    "Unable to load transaction history."
                );

            } finally {

                setLoading(false);
            }
        };


    /* =====================================================
       INITIAL LOAD
    ===================================================== */

    useEffect(() => {

        fetchTransactions();

    }, []);


    /* =====================================================
       SEARCH
    ===================================================== */

    const filteredTransactions =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();


            if (!keyword) {

                return income;
            }


            return income.filter(
                (item) => {

                    const status =
                        getStatus(
                            item.status
                        );


                    const searchable = [

                        item.id,

                        item.amount,

                        item.before_amount,

                        item.after_amount,

                        item.date,

                        item.transction_Type,

                        item.transaction_Type,

                        item.note,

                        status.text

                    ]
                        .filter(
                            (
                                value
                            ) =>
                                value !==
                                    undefined &&
                                value !==
                                    null
                        )
                        .join(" ")
                        .toLowerCase();


                    return searchable.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    /* =====================================================
       RESET PAGE WHEN SEARCHING
    ===================================================== */

    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredTransactions.length /
                PER_PAGE
            )
        );


    useEffect(() => {

        if (
            currentPage >
            totalPages
        ) {

            setCurrentPage(
                totalPages
            );
        }

    }, [
        currentPage,
        totalPages
    ]);


    const currentTransactions =
        useMemo(() => {

            const start =
                (
                    currentPage -
                    1
                ) *
                PER_PAGE;


            return filteredTransactions.slice(
                start,
                start +
                PER_PAGE
            );

        }, [
            filteredTransactions,
            currentPage
        ]);


    /* =====================================================
       CHANGE PAGE
    ===================================================== */

    const changePage = (page) => {

        const finalPage =
            Math.max(
                1,
                Math.min(
                    page,
                    totalPages
                )
            );


        setCurrentPage(
            finalPage
        );


        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };


    /* =====================================================
       PAGE NUMBERS
    ===================================================== */

    const pageNumbers =
        useMemo(() => {

            const pages = [];


            let start =
                Math.max(
                    1,
                    currentPage - 2
                );


            let end =
                Math.min(
                    totalPages,
                    currentPage + 2
                );


            /*
             * Try to show 5 page buttons
             */

            if (
                end - start < 4
            ) {

                if (start === 1) {

                    end =
                        Math.min(
                            totalPages,
                            5
                        );

                } else if (
                    end === totalPages
                ) {

                    start =
                        Math.max(
                            1,
                            totalPages - 4
                        );
                }
            }


            for (
                let i = start;
                i <= end;
                i++
            ) {

                pages.push(i);
            }


            return pages;

        }, [
            currentPage,
            totalPages
        ]);


    /* =====================================================
       SUMMARY
    ===================================================== */

    const summary =
        useMemo(() => {

            let totalAmount = 0;

            let successfulAmount = 0;

            let successful = 0;

            let pending = 0;

            let failed = 0;


            income.forEach(
                (item) => {

                    const amount =
                        Number(
                            item?.amount ||
                            0
                        );


                    const status =
                        getStatus(
                            item.status
                        );


                    totalAmount +=
                        amount;


                    if (
                        status.className ===
                        "success"
                    ) {

                        successful++;

                        successfulAmount +=
                            amount;
                    }


                    if (
                        status.className ===
                        "pending"
                    ) {

                        pending++;
                    }


                    if (
                        status.className ===
                            "failed" ||
                        status.className ===
                            "cancelled"
                    ) {

                        failed++;
                    }
                }
            );


            return {

                totalTransactions:
                    income.length,

                totalAmount:
                    totalAmount,

                successfulAmount:
                    successfulAmount,

                successful:
                    successful,

                pending:
                    pending,

                failed:
                    failed

            };

        }, [income]);


    /* =====================================================
       SHOWING COUNT
    ===================================================== */

    const showingFrom =
        filteredTransactions.length === 0
            ? 0
            : (
                (
                    currentPage -
                    1
                ) *
                PER_PAGE
            ) + 1;


    const showingTo =
        Math.min(
            currentPage *
            PER_PAGE,

            filteredTransactions.length
        );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                CSS
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .transaction-history-page {

                    --primary: #5d50f5;
                    --primary-dark: #493be7;
                    --primary-soft: #f2f0ff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --orange: #f79009;
                    --orange-dark: #b54708;
                    --orange-soft: #fff8e8;

                    --red: #f04438;
                    --red-dark: #b42318;
                    --red-soft: #fef3f2;

                    --blue: #1570ef;
                    --blue-soft: #eff8ff;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .transaction-container {

                    width: 100%;

                    max-width: 1450px;

                    margin: 0 auto;
                }


                /* =============================================
                   HEADER
                ============================================= */

                .transaction-page-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 22px;
                }


                .transaction-page-title h2 {

                    margin:
                        0 0 6px;

                    color:
                        var(--text);

                    font-size: 28px;

                    line-height: 1.2;

                    font-weight: 800;
                }


                .transaction-page-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 14px;
                }


                .transaction-record-badge {

                    flex-shrink: 0;

                    padding:
                        10px 16px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 12px;

                    background: #ffffff;

                    color:
                        var(--text2);

                    font-size: 13px;

                    font-weight: 700;
                }


                /* =============================================
                   SUMMARY
                ============================================= */

                .transaction-summary-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            4,
                            minmax(0, 1fr)
                        );

                    gap: 14px;

                    margin-bottom: 20px;
                }


                .transaction-summary-card {

                    padding: 19px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 16px;

                    background: #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .04
                        );
                }


                .transaction-summary-top {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 12px;

                    margin-bottom: 14px;
                }


                .transaction-summary-label {

                    color:
                        var(--muted);

                    font-size: 13px;

                    font-weight: 600;
                }


                .transaction-summary-icon {

                    width: 42px;
                    height: 42px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 12px;

                    font-size: 17px;

                    font-weight: 800;
                }


                .transaction-summary-icon.purple {

                    color:
                        var(--primary);

                    background:
                        var(--primary-soft);
                }


                .transaction-summary-icon.green {

                    color:
                        var(--green-dark);

                    background:
                        var(--green-soft);
                }


                .transaction-summary-icon.orange {

                    color:
                        var(--orange-dark);

                    background:
                        var(--orange-soft);
                }


                .transaction-summary-icon.red {

                    color:
                        var(--red-dark);

                    background:
                        var(--red-soft);
                }


                .transaction-summary-value {

                    display: block;

                    color:
                        var(--text);

                    font-size: 23px;

                    line-height: 1.25;

                    font-weight: 800;

                    word-break: break-word;
                }


                .transaction-summary-value.green {

                    color:
                        var(--green-dark);
                }


                .transaction-summary-value.orange {

                    color:
                        var(--orange-dark);
                }


                .transaction-summary-value.red {

                    color:
                        var(--red-dark);
                }


                .transaction-summary-note {

                    display: block;

                    margin-top: 6px;

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                /* =============================================
                   MAIN CARD
                ============================================= */

                .transaction-main-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 18px;

                    background: #ffffff;

                    box-shadow:
                        0 8px 30px
                        rgba(
                            16,
                            24,
                            40,
                            .05
                        );
                }


                /* =============================================
                   TOOLBAR
                ============================================= */

                .transaction-toolbar {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    padding: 19px 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .transaction-toolbar-title strong {

                    display: block;

                    margin-bottom: 3px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .transaction-toolbar-title span {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .transaction-search {

                    position: relative;

                    width: 350px;

                    max-width: 100%;
                }


                .transaction-search-icon {

                    position: absolute;

                    top: 50%;
                    left: 15px;

                    transform:
                        translateY(-50%);

                    color: #98a2b3;

                    font-size: 15px;

                    pointer-events: none;
                }


                .transaction-search input {

                    width: 100%;
                    height: 46px;

                    padding:
                        0 15px
                        0 42px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    outline: none;

                    background:
                        #f9fafb;

                    color:
                        var(--text);

                    font-size: 14px;

                    transition:
                        .2s ease;
                }


                .transaction-search input:focus {

                    border-color:
                        var(--primary);

                    background:
                        #ffffff;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            93,
                            80,
                            245,
                            .08
                        );
                }


                /* =============================================
                   TABLE
                ============================================= */

                .transaction-table-responsive {

                    width: 100%;

                    overflow-x: auto;
                }


                .transaction-table {

                    width: 100%;

                    min-width: 950px;

                    margin: 0;

                    border-collapse: collapse;
                }


                .transaction-table thead {

                    background:
                        #f9fafb;
                }


                .transaction-table th {

                    padding:
                        14px 17px;

                    border-bottom:
                        1px solid
                        var(--border);

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 800;

                    text-align: left;

                    text-transform: uppercase;

                    letter-spacing: .4px;

                    white-space: nowrap;
                }


                .transaction-table td {

                    padding:
                        17px;

                    border-bottom:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--text2);

                    font-size: 13px;

                    vertical-align: middle;
                }


                .transaction-table tbody tr {

                    transition:
                        .15s ease;
                }


                .transaction-table tbody tr:hover {

                    background:
                        #fafaff;
                }


                .transaction-id {

                    min-width: 38px;
                    height: 32px;

                    display:
                        inline-flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 9px;

                    border-radius: 8px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 12px;

                    font-weight: 800;
                }


                .transaction-amount {

                    color:
                        var(--text);

                    font-size: 14px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                .transaction-before {

                    color:
                        var(--muted);

                    font-weight: 600;

                    white-space: nowrap;
                }


                .transaction-after {

                    color:
                        var(--text2);

                    font-weight: 700;

                    white-space: nowrap;
                }


                .transaction-date {

                    color:
                        var(--muted);

                    font-size: 12px;

                    white-space: nowrap;
                }


                /* =============================================
                   STATUS
                ============================================= */

                .transaction-status {

                    display:
                        inline-flex;

                    align-items: center;

                    gap: 6px;

                    padding:
                        6px 10px;

                    border-radius: 50px;

                    font-size: 11px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                .transaction-status-dot {

                    width: 6px;
                    height: 6px;

                    border-radius: 50%;

                    background: currentColor;
                }


                .transaction-status.success {

                    color:
                        var(--green-dark);

                    background:
                        var(--green-soft);
                }


                .transaction-status.failed {

                    color:
                        var(--red-dark);

                    background:
                        var(--red-soft);
                }


                .transaction-status.pending {

                    color:
                        var(--orange-dark);

                    background:
                        var(--orange-soft);
                }


                .transaction-status.cancelled {

                    color: #475467;

                    background: #f2f4f7;
                }


                .transaction-status.default {

                    color:
                        var(--primary);

                    background:
                        var(--primary-soft);
                }


                /* =============================================
                   LOADING / EMPTY
                ============================================= */

                .transaction-message {

                    padding:
                        65px 20px;

                    text-align: center;
                }


                .transaction-message-icon {

                    width: 58px;
                    height: 58px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin:
                        0 auto 14px;

                    border-radius: 16px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 22px;
                }


                .transaction-message strong {

                    display: block;

                    margin-bottom: 6px;

                    color:
                        var(--text);

                    font-size: 17px;
                }


                .transaction-message span {

                    color:
                        var(--muted);

                    font-size: 13px;
                }


                .transaction-spinner {

                    width: 38px;
                    height: 38px;

                    margin:
                        0 auto 15px;

                    border:
                        4px solid
                        #ececf1;

                    border-top-color:
                        var(--primary);

                    border-radius: 50%;

                    animation:
                        transactionSpin
                        .8s linear
                        infinite;
                }


                @keyframes transactionSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                /* =============================================
                   FOOTER
                ============================================= */

                .transaction-footer {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        17px 20px;

                    border-top:
                        1px solid
                        #eef0f4;
                }


                .transaction-showing {

                    color:
                        var(--muted);

                    font-size: 13px;
                }


                .transaction-showing strong {

                    color:
                        var(--text2);

                    font-weight: 700;
                }


                /* =============================================
                   PAGINATION
                ============================================= */

                .transaction-pagination {

                    display: flex;

                    align-items: center;

                    flex-wrap: wrap;

                    gap: 6px;
                }


                .transaction-page-button {

                    min-width: 38px;
                    height: 38px;

                    display:
                        inline-flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 11px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 9px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    font-size: 13px;

                    font-weight: 700;

                    cursor: pointer;

                    transition:
                        .15s ease;
                }


                .transaction-page-button:hover:not(:disabled) {

                    border-color:
                        var(--primary);

                    color:
                        var(--primary);

                    background:
                        var(--primary-soft);
                }


                .transaction-page-button.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color: #ffffff;
                }


                .transaction-page-button:disabled {

                    opacity: .4;

                    cursor: not-allowed;
                }


                /* =============================================
                   MOBILE
                ============================================= */

                .transaction-mobile-list {

                    display: none;
                }


                .transaction-mobile-card {

                    padding: 16px;

                    margin-bottom: 12px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 14px;

                    background: #ffffff;
                }


                .transaction-mobile-card:last-child {

                    margin-bottom: 0;
                }


                .transaction-mobile-head {

                    display: flex;

                    align-items:
                        flex-start;

                    justify-content:
                        space-between;

                    gap: 12px;

                    padding-bottom: 13px;

                    margin-bottom: 10px;

                    border-bottom:
                        1px solid
                        #f0f1f4;
                }


                .transaction-mobile-number {

                    display: block;

                    margin-bottom: 4px;

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 600;
                }


                .transaction-mobile-amount {

                    color:
                        var(--text);

                    font-size: 18px;

                    font-weight: 800;
                }


                .transaction-mobile-row {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        8px 0;
                }


                .transaction-mobile-row span {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .transaction-mobile-row strong {

                    color:
                        var(--text2);

                    font-size: 13px;

                    text-align: right;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 1100px
                ) {

                    .transaction-summary-grid {

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );
                    }
                }


                @media (
                    max-width: 767px
                ) {

                    .transaction-history-page {

                        padding: 14px;

                        border-radius: 14px;
                    }


                    .transaction-page-header {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .transaction-page-title h2 {

                        font-size: 23px;
                    }


                    .transaction-summary-grid {

                        grid-template-columns:
                            1fr;

                        gap: 10px;
                    }


                    .transaction-summary-card {

                        padding: 16px;
                    }


                    .transaction-toolbar {

                        align-items:
                            stretch;

                        flex-direction:
                            column;

                        padding: 16px;
                    }


                    .transaction-search {

                        width: 100%;
                    }


                    .transaction-desktop {

                        display: none;
                    }


                    .transaction-mobile-list {

                        display: block;

                        padding: 14px;

                        background:
                            #f9fafb;
                    }


                    .transaction-footer {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .transaction-pagination {

                        width: 100%;

                        justify-content:
                            center;
                    }
                }


                @media (
                    max-width: 420px
                ) {

                    .transaction-history-page {

                        padding: 10px;
                    }


                    .transaction-page-title h2 {

                        font-size: 21px;
                    }


                    .transaction-summary-value {

                        font-size: 20px;
                    }


                    .transaction-page-button {

                        min-width: 34px;
                        height: 35px;

                        padding:
                            0 8px;
                    }
                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="transaction-history-page"
            >

                <div
                    className="transaction-container"
                >


                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <div
                        className="transaction-page-header"
                    >

                        <div
                            className="transaction-page-title"
                        >

                            <h2>
                                {t(
                                    "pro_TrHis_tr_his"
                                ) ||
                                    "Transaction History"}
                            </h2>


                            <p>
                                View and track all your
                                account transactions.
                            </p>

                        </div>


                        <div
                            className="transaction-record-badge"
                        >
                            {income.length} Transactions
                        </div>

                    </div>


                    {/* =========================================
                        SUMMARY
                    ========================================= */}

                    <div
                        className="transaction-summary-grid"
                    >


                        {/* TOTAL TRANSACTIONS */}

                        <div
                            className="transaction-summary-card"
                        >

                            <div
                                className="transaction-summary-top"
                            >

                                <span
                                    className="transaction-summary-label"
                                >
                                    Total Transactions
                                </span>


                                <div
                                    className="transaction-summary-icon purple"
                                >
                                    #
                                </div>

                            </div>


                            <strong
                                className="transaction-summary-value"
                            >
                                {numberFormat(
                                    summary
                                        .totalTransactions
                                )}
                            </strong>


                            <span
                                className="transaction-summary-note"
                            >
                                All transaction records
                            </span>

                        </div>


                        {/* TOTAL AMOUNT */}

                        <div
                            className="transaction-summary-card"
                        >

                            <div
                                className="transaction-summary-top"
                            >

                                <span
                                    className="transaction-summary-label"
                                >
                                    Total Amount
                                </span>


                                <div
                                    className="transaction-summary-icon green"
                                >
                                    {currencySymbol}
                                </div>

                            </div>


                            <strong
                                className="transaction-summary-value green"
                            >
                                {money(
                                    summary.totalAmount,
                                    currencySymbol
                                )}
                            </strong>


                            <span
                                className="transaction-summary-note"
                            >
                                Transaction volume
                            </span>

                        </div>


                        {/* PENDING */}

                        <div
                            className="transaction-summary-card"
                        >

                            <div
                                className="transaction-summary-top"
                            >

                                <span
                                    className="transaction-summary-label"
                                >
                                    Pending
                                </span>


                                <div
                                    className="transaction-summary-icon orange"
                                >
                                    !
                                </div>

                            </div>


                            <strong
                                className="transaction-summary-value orange"
                            >
                                {numberFormat(
                                    summary.pending
                                )}
                            </strong>


                            <span
                                className="transaction-summary-note"
                            >
                                Waiting for completion
                            </span>

                        </div>


                        {/* SUCCESS */}

                        <div
                            className="transaction-summary-card"
                        >

                            <div
                                className="transaction-summary-top"
                            >

                                <span
                                    className="transaction-summary-label"
                                >
                                    Successful
                                </span>


                                <div
                                    className="transaction-summary-icon green"
                                >
                                    ✓
                                </div>

                            </div>


                            <strong
                                className="transaction-summary-value green"
                            >
                                {numberFormat(
                                    summary.successful
                                )}
                            </strong>


                            <span
                                className="transaction-summary-note"
                            >
                                Completed transactions
                            </span>

                        </div>

                    </div>


                    {/* =========================================
                        MAIN
                    ========================================= */}

                    <div
                        className="transaction-main-card"
                    >


                        {/* TOOLBAR */}

                        <div
                            className="transaction-toolbar"
                        >

                            <div
                                className="transaction-toolbar-title"
                            >

                                <strong>
                                    Transaction Records
                                </strong>


                                <span>
                                    Complete account
                                    transaction history
                                </span>

                            </div>


                            <div
                                className="transaction-search"
                            >

                                <span
                                    className="transaction-search-icon"
                                >
                                    <i
                                        className="fa fa-search"
                                        aria-hidden="true"
                                    ></i>
                                </span>


                                <input
                                    type="text"
                                    value={search}
                                    placeholder="Search transactions..."
                                    onChange={
                                        (event) =>
                                            setSearch(
                                                event
                                                    .target
                                                    .value
                                            )
                                    }
                                />

                            </div>

                        </div>


                        {/* =====================================
                            LOADING
                        ===================================== */}

                        {loading ? (

                            <div
                                className="transaction-message"
                            >

                                <div
                                    className="transaction-spinner"
                                ></div>


                                <strong>
                                    Loading transactions...
                                </strong>


                                <span>
                                    Please wait while
                                    your history is loaded.
                                </span>

                            </div>

                        ) : pageError ? (


                            /* =================================
                               ERROR
                            ================================= */

                            <div
                                className="transaction-message"
                            >

                                <div
                                    className="transaction-message-icon"
                                >
                                    !
                                </div>


                                <strong>
                                    Unable to load transactions
                                </strong>


                                <span>
                                    {pageError}
                                </span>

                            </div>

                        ) : currentTransactions.length ===
                            0 ? (


                            /* =================================
                               EMPTY
                            ================================= */

                            <div
                                className="transaction-message"
                            >

                                <div
                                    className="transaction-message-icon"
                                >
                                    <i
                                        className="fa fa-exchange"
                                        aria-hidden="true"
                                    ></i>
                                </div>


                                <strong>
                                    No transactions found
                                </strong>


                                <span>

                                    {search
                                        ? "No transactions match your search."
                                        : "Your transaction records will appear here."
                                    }

                                </span>

                            </div>

                        ) : (

                            <Fragment>


                                {/* =============================
                                    DESKTOP TABLE
                                ============================= */}

                                <div
                                    className="transaction-desktop"
                                >

                                    <div
                                        className="transaction-table-responsive"
                                    >

                                        <table
                                            className="transaction-table"
                                        >

                                            <thead>

                                                <tr>

                                                    <th>
                                                        ID
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_TrHis_amount"
                                                        ) ||
                                                            "Amount"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_TrHis_bef_amo"
                                                        ) ||
                                                            "Before Amount"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_TrHis_aft_amo"
                                                        ) ||
                                                            "After Amount"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_TrHis_status"
                                                        ) ||
                                                            "Status"}
                                                    </th>

                                                    <th>
                                                        {t(
                                                            "pro_TrHis_date"
                                                        ) ||
                                                            "Date"}
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {currentTransactions.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => {

                                                        const serial =
                                                            (
                                                                (
                                                                    currentPage -
                                                                    1
                                                                ) *
                                                                PER_PAGE
                                                            ) +
                                                            index +
                                                            1;


                                                        const status =
                                                            getStatus(
                                                                row.status
                                                            );


                                                        return (

                                                            <tr
                                                                key={
                                                                    row.id ||
                                                                    serial
                                                                }
                                                            >


                                                                {/* ID */}

                                                                <td>

                                                                    <span
                                                                        className="transaction-id"
                                                                    >
                                                                        {serial}
                                                                    </span>

                                                                </td>


                                                                {/* AMOUNT */}

                                                                <td>

                                                                    <span
                                                                        className="transaction-amount"
                                                                    >
                                                                        {money(
                                                                            row.amount,
                                                                            currencySymbol
                                                                        )}
                                                                    </span>

                                                                </td>


                                                                {/* BEFORE */}

                                                                <td>

                                                                    <span
                                                                        className="transaction-before"
                                                                    >
                                                                        {money(
                                                                            row.before_amount,
                                                                            currencySymbol
                                                                        )}
                                                                    </span>

                                                                </td>


                                                                {/* AFTER */}

                                                                <td>

                                                                    <span
                                                                        className="transaction-after"
                                                                    >
                                                                        {money(
                                                                            row.after_amount,
                                                                            currencySymbol
                                                                        )}
                                                                    </span>

                                                                </td>


                                                                {/* STATUS */}

                                                                <td>

                                                                    <span
                                                                        className={
                                                                            `transaction-status ${status.className}`
                                                                        }
                                                                    >

                                                                        <span
                                                                            className="transaction-status-dot"
                                                                        ></span>

                                                                        {
                                                                            status.text
                                                                        }

                                                                    </span>

                                                                </td>


                                                                {/* DATE */}

                                                                <td>

                                                                    <span
                                                                        className="transaction-date"
                                                                    >
                                                                        {formatDate(
                                                                            row.date
                                                                        )}
                                                                    </span>

                                                                </td>

                                                            </tr>
                                                        );
                                                    }
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>


                                {/* =============================
                                    MOBILE VIEW
                                ============================= */}

                                <div
                                    className="transaction-mobile-list"
                                >

                                    {currentTransactions.map(
                                        (
                                            row,
                                            index
                                        ) => {

                                            const serial =
                                                (
                                                    (
                                                        currentPage -
                                                        1
                                                    ) *
                                                    PER_PAGE
                                                ) +
                                                index +
                                                1;


                                            const status =
                                                getStatus(
                                                    row.status
                                                );


                                            return (

                                                <div
                                                    className="transaction-mobile-card"
                                                    key={
                                                        row.id ||
                                                        serial
                                                    }
                                                >


                                                    {/* HEADER */}

                                                    <div
                                                        className="transaction-mobile-head"
                                                    >

                                                        <div>

                                                            <span
                                                                className="transaction-mobile-number"
                                                            >
                                                                Transaction #{serial}
                                                            </span>


                                                            <div
                                                                className="transaction-mobile-amount"
                                                            >
                                                                {money(
                                                                    row.amount,
                                                                    currencySymbol
                                                                )}
                                                            </div>

                                                        </div>


                                                        <span
                                                            className={
                                                                `transaction-status ${status.className}`
                                                            }
                                                        >

                                                            <span
                                                                className="transaction-status-dot"
                                                            ></span>

                                                            {
                                                                status.text
                                                            }

                                                        </span>

                                                    </div>


                                                    {/* BEFORE */}

                                                    <div
                                                        className="transaction-mobile-row"
                                                    >

                                                        <span>
                                                            Before Amount
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                row.before_amount,
                                                                currencySymbol
                                                            )}
                                                        </strong>

                                                    </div>


                                                    {/* AMOUNT */}

                                                    <div
                                                        className="transaction-mobile-row"
                                                    >

                                                        <span>
                                                            Transaction Amount
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                row.amount,
                                                                currencySymbol
                                                            )}
                                                        </strong>

                                                    </div>


                                                    {/* AFTER */}

                                                    <div
                                                        className="transaction-mobile-row"
                                                    >

                                                        <span>
                                                            After Amount
                                                        </span>

                                                        <strong>
                                                            {money(
                                                                row.after_amount,
                                                                currencySymbol
                                                            )}
                                                        </strong>

                                                    </div>


                                                    {/* DATE */}

                                                    <div
                                                        className="transaction-mobile-row"
                                                    >

                                                        <span>
                                                            Date
                                                        </span>

                                                        <strong>
                                                            {formatDate(
                                                                row.date
                                                            )}
                                                        </strong>

                                                    </div>

                                                </div>
                                            );
                                        }
                                    )}

                                </div>

                            </Fragment>
                        )}


                        {/* =====================================
                            FOOTER
                        ===================================== */}

                        {!loading &&
                            !pageError &&
                            filteredTransactions.length >
                                0 && (

                            <div
                                className="transaction-footer"
                            >


                                {/* SHOWING */}

                                <div
                                    className="transaction-showing"
                                >

                                    Showing{" "}

                                    <strong>
                                        {showingFrom}
                                    </strong>

                                    {" "}to{" "}

                                    <strong>
                                        {showingTo}
                                    </strong>

                                    {" "}of{" "}

                                    <strong>
                                        {
                                            filteredTransactions
                                                .length
                                        }
                                    </strong>

                                    {" "}entries

                                </div>


                                {/* PAGINATION */}

                                <div
                                    className="transaction-pagination"
                                >


                                    {/* FIRST */}

                                    <button
                                        type="button"
                                        className="transaction-page-button"
                                        disabled={
                                            currentPage ===
                                            1
                                        }
                                        onClick={() =>
                                            changePage(
                                                1
                                            )
                                        }
                                        title="First Page"
                                    >

                                        <i
                                            className="fa fa-angle-double-left"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* PREVIOUS */}

                                    <button
                                        type="button"
                                        className="transaction-page-button"
                                        disabled={
                                            currentPage ===
                                            1
                                        }
                                        onClick={() =>
                                            changePage(
                                                currentPage -
                                                1
                                            )
                                        }
                                        title="Previous Page"
                                    >

                                        <i
                                            className="fa fa-angle-left"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* PAGE NUMBERS */}

                                    {pageNumbers.map(
                                        (page) => (

                                            <button
                                                type="button"
                                                key={page}
                                                className={
                                                    `transaction-page-button ${
                                                        currentPage ===
                                                        page
                                                            ? "active"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    changePage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>

                                        )
                                    )}


                                    {/* NEXT */}

                                    <button
                                        type="button"
                                        className="transaction-page-button"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            changePage(
                                                currentPage +
                                                1
                                            )
                                        }
                                        title="Next Page"
                                    >

                                        <i
                                            className="fa fa-angle-right"
                                            aria-hidden="true"
                                        ></i>

                                    </button>


                                    {/* LAST */}

                                    <button
                                        type="button"
                                        className="transaction-page-button"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            changePage(
                                                totalPages
                                            )
                                        }
                                        title="Last Page"
                                    >

                                        <i
                                            className="fa fa-angle-double-right"
                                            aria-hidden="true"
                                        ></i>

                                    </button>

                                </div>

                            </div>
                        )}

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default BasicDatatable;